import type { FleetProjectSummary, GatewaySessionRow, SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { cardsFromSpawnedSessions } from "../controllers/subagents.ts";
import { projectsForChatContext } from "./fleet-projects-session.ts";
import { resolveAgentIdForSession } from "./resolve-session-agent.ts";
import { filterSubagentRunCards, subagentRunTitle } from "./subagent-run-display.ts";
import { resolveSubagentActivityPhase } from "./subagent-run-status.ts";

export type DelegationGraphNode = {
  id: string;
  label: string;
  subtitle?: string;
  kind: "parent" | "project" | "subagent" | "orchestration";
  status?: ChatSubagentCard["status"];
  /**
   * True when the raw card status is "running" / "queued" / "spawning"
   * but `resolveSubagentActivityPhase` has demoted the card to "idle"
   * (typically because it was restored from disk after a restart with
   * no runId and no fresh activity). When true, the Fleet Map badge
   * should render as `idle` and `node.active` MUST be false even though
   * `node.status` is still the raw value (we keep the raw value so
   * callers that don't know about demotion still get the truthful
   * last-known wire state).
   */
  idleDemoted?: boolean;
  /**
   * JAISIU-types-sweep: derived liveness flag — `true` when the node's
   * status is active at render time (running / queued / spawning /
   * waiting-confirmation, NOT demoted to idle). Computed alongside
   * the node itself by `flattenDelegationGraph`, so the Fleet Map's
   * badge color is a single property lookup at render time. Tests in
   * `session-delegation-graph.node.test.ts` assert the relationship
   * with `node.status` to catch regressions in the demotion logic.
   */
  active?: boolean;
  task?: string;
  /** Canonical session key for navigation */
  sessionKey?: string;
  spawnedBy?: string | null;
  childSessionKeys: string[];
  depth: number;
  cardId?: string;
  agentId?: string;
};

export type DelegationGraphEdge = {
  from: string;
  to: string;
  label?: string;
  childSessionKey?: string;
  active?: boolean;
  /** Subagent spawned another subagent */
  nested?: boolean;
};

export type SessionDelegationGraph = {
  fleetRootSessionKey: string;
  focusSessionKey: string;
  parentAgentId: string;
  parentLabel: string;
  nodes: DelegationGraphNode[];
  edges: DelegationGraphEdge[];
  /** Nodes grouped by depth for tree layout */
  levels: DelegationGraphNode[][];
};

function isSubagentSessionKey(key: string): boolean {
  return key.includes(":subagent:");
}

function indexSessions(sessions: GatewaySessionRow[]): Map<string, GatewaySessionRow> {
  const map = new Map<string, GatewaySessionRow>();
  for (const row of sessions) {
    const key = row.key?.trim();
    if (key) {
      map.set(key, row);
    }
  }
  return map;
}

/** Walk spawnedBy upward to the top of this delegation chain. */
export function findFleetDelegationRoot(sessionKey: string, sessions: GatewaySessionRow[]): string {
  const byKey = indexSessions(sessions);
  let current = sessionKey.trim();
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    seen.add(current);
    const row = byKey.get(current);
    const parent = row?.spawnedBy?.trim();
    if (!parent || !byKey.has(parent)) {
      break;
    }
    current = parent;
  }
  return current || sessionKey;
}

/** All sessions in the subtree rooted at rootKey (direct + nested spawns). */
export function collectSpawnedSessionKeys(
  rootKey: string,
  sessions: GatewaySessionRow[],
): string[] {
  const byKey = indexSessions(sessions);
  const out: string[] = [];
  const queue = [rootKey.trim()];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const parent = queue.shift();
    if (!parent || seen.has(parent)) {
      continue;
    }
    seen.add(parent);
    for (const row of sessions) {
      const key = row.key?.trim();
      if (!key || row.spawnedBy?.trim() !== parent) {
        continue;
      }
      out.push(key);
      queue.push(key);
    }
  }
  return out;
}

function mergeCardsForTree(
  fleetRootKey: string,
  subagents: ChatSubagentCard[],
  sessions: SessionsListResult | null,
): ChatSubagentCard[] {
  const list = sessions?.sessions ?? [];
  const byKey = new Map<string, ChatSubagentCard>();
  const allKeys = new Set([fleetRootKey, ...collectSpawnedSessionKeys(fleetRootKey, list)]);

  for (const key of allKeys) {
    for (const c of cardsFromSpawnedSessions(key, sessions)) {
      if (c.childSessionKey) {
        byKey.set(c.childSessionKey, c);
      }
    }
  }
  for (const c of subagents) {
    if (c.childSessionKey) {
      byKey.set(c.childSessionKey, c);
    }
  }
  return [...byKey.values()];
}

function nodeIdForSession(sessionKey: string): string {
  return `session:${sessionKey}`;
}

function resolveCardStatus(card?: ChatSubagentCard): ChatSubagentCard["status"] | undefined {
  return card?.status;
}

/**
 * Status implied by a session row's own terminal signals.
 *
 * `GatewaySessionRow` has no `status` field: `sessions.list` reports an
 * operator cancel as `abortedLastRun` and a crashed/errored run as
 * `lastRunError`. A row with neither gives us nothing to say, so the status
 * stays undefined rather than guessing "running" — an evicted child's
 * settled-ness is not knowable from the row alone.
 */
function rowStatusOf(row?: GatewaySessionRow): ChatSubagentCard["status"] | undefined {
  if (!row) return undefined;
  if (row.abortedLastRun === true) return "cancelled";
  if (typeof row.lastRunError === "string") return "error";
  return undefined;
}

function isActiveStatus(status?: ChatSubagentCard["status"]): boolean {
  return status === "running" || status === "spawning" || status === "queued";
}

export function buildSessionDelegationGraph(params: {
  sessionKey: string;
  subagents: ChatSubagentCard[];
  sessions: SessionsListResult | null;
  projects?: FleetProjectSummary[];
  /**
   * JAISIU-types-sweep: per-session gateway defaults (model
   * provider, context window, send policy) forwarded through the
   * delegation graph so the chat-history preview chips carry the
   * same model context as the row. Optional on the type — the
   * builder coalesces missing values with `params.sessions?.defaults`
   * and falls back to `null` when neither is present.
   */
  defaults?: import("../types.ts").GatewaySessionsDefaults | null;
}): SessionDelegationGraph {
  const focusSessionKey = params.sessionKey.trim();
  const sessionRows = params.sessions?.sessions ?? [];
  const fleetRootSessionKey = findFleetDelegationRoot(focusSessionKey, sessionRows);
  const parentAgentId = resolveAgentIdForSession(fleetRootSessionKey);
  const parentId = `parent:${parentAgentId}`;
  const cards = filterSubagentRunCards(
    mergeCardsForTree(fleetRootSessionKey, params.subagents, params.sessions),
  );
  const cardBySession = new Map<string, ChatSubagentCard>();
  for (const card of cards) {
    if (card.childSessionKey) {
      cardBySession.set(card.childSessionKey, card);
    }
  }
  const projects = projectsForChatContext(params.projects ?? [], fleetRootSessionKey);

  const nodes: DelegationGraphNode[] = [];
  const edges: DelegationGraphEdge[] = [];
  const childKeysByParent = new Map<string, string[]>();

  const registerChild = (parentKey: string, childKey: string) => {
    const list = childKeysByParent.get(parentKey) ?? [];
    if (!list.includes(childKey)) {
      list.push(childKey);
      childKeysByParent.set(parentKey, list);
    }
  };

  nodes.push({
    id: parentId,
    label: parentAgentId === "main" ? "Parent chat" : parentAgentId,
    subtitle: "Fleet root",
    kind: "parent",
    sessionKey: fleetRootSessionKey,
    spawnedBy: null,
    childSessionKeys: [],
    depth: 0,
    agentId: parentAgentId,
  });

  for (const project of projects) {
    const projectNodeId = `project:${project.id}`;
    const projectKey = project.sessionKey?.trim() ?? "";
    nodes.push({
      id: projectNodeId,
      label: project.name,
      subtitle: "Project workspace",
      kind: "project",
      sessionKey: projectKey || undefined,
      spawnedBy: fleetRootSessionKey,
      childSessionKeys: [],
      depth: 1,
    });
    edges.push({
      from: parentId,
      to: projectNodeId,
      label: "project",
      childSessionKey: projectKey || undefined,
    });
    if (projectKey) {
      registerChild(fleetRootSessionKey, projectKey);
    }
  }

  const spawnedKeys = collectSpawnedSessionKeys(fleetRootSessionKey, sessionRows);
  for (const card of cards) {
    const key = card.childSessionKey?.trim();
    if (key && !spawnedKeys.includes(key)) {
      spawnedKeys.push(key);
    }
  }
  const orchGroups = new Map<string, ChatSubagentCard[]>();
  for (const card of cards) {
    if (card.orchestrationId && card.childSessionKey) {
      const parentSpawn =
        sessionRows.find((r) => r.key === card.childSessionKey)?.spawnedBy?.trim() ??
        fleetRootSessionKey;
      if (parentSpawn === fleetRootSessionKey) {
        const list = orchGroups.get(card.orchestrationId) ?? [];
        list.push(card);
        orchGroups.set(card.orchestrationId, list);
      }
    }
  }

  const orchNodeIds = new Map<string, string>();
  for (const [orchId, group] of orchGroups) {
    const orchNodeId = `orch:${orchId}`;
    orchNodeIds.set(orchId, orchNodeId);
    nodes.push({
      id: orchNodeId,
      label: `Orchestration · ${group.length}`,
      subtitle: group[0]?.task?.trim().slice(0, 80),
      kind: "orchestration",
      task: group[0]?.task,
      spawnedBy: fleetRootSessionKey,
      childSessionKeys: group.map((c) => c.childSessionKey).filter((k): k is string => Boolean(k)),
      depth: 1,
    });
    edges.push({
      from: parentId,
      to: orchNodeId,
      label: "orchestrate",
      active: group.some((c) => isActiveStatus(c.status)),
    });
    for (const c of group) {
      if (c.childSessionKey) {
        registerChild(orchNodeId, c.childSessionKey);
      }
    }
  }

  const byKey = indexSessions(sessionRows);
  const depthBySession = new Map<string, number>();
  depthBySession.set(fleetRootSessionKey, 0);
  const depthQueue = [fleetRootSessionKey];
  while (depthQueue.length > 0) {
    const parentKey = depthQueue.shift();
    if (!parentKey) {
      continue;
    }
    const parentDepth = depthBySession.get(parentKey) ?? 0;
    for (const key of spawnedKeys) {
      if (byKey.get(key)?.spawnedBy?.trim() !== parentKey) {
        continue;
      }
      if (!depthBySession.has(key)) {
        depthBySession.set(key, parentDepth + 1);
        depthQueue.push(key);
      }
    }
  }

  for (const sessionKey of spawnedKeys) {
    if (!isSubagentSessionKey(sessionKey)) {
      continue;
    }
    const row = byKey.get(sessionKey);
    const card = cardBySession.get(sessionKey);
    const spawnedBy = row?.spawnedBy?.trim() ?? fleetRootSessionKey;
    let depth = depthBySession.get(sessionKey) ?? 1;
    if (
      card?.orchestrationId &&
      spawnedBy === fleetRootSessionKey &&
      orchNodeIds.has(card.orchestrationId)
    ) {
      depth = Math.max(depth, 2);
    }
    const nodeId = nodeIdForSession(sessionKey);
    const agentId = card?.agentId ?? resolveAgentIdForSession(sessionKey);
    const cardStatus = resolveCardStatus(card);
    // JAISIU-fleet-map-status — when no card matches the session row the
    // card-derived status is undefined. Fall back to the status implied by
    // the row's real terminal signals: `sessions.list` reports an operator
    // cancel as `abortedLastRun` and a crashed run as `lastRunError`, and it
    // has no `status` field at all. The previous `row?.status` read (laundered
    // into the type with a cast) was therefore always undefined — dead code.
    // Same mapping `cardsFromSpawnedSessions` applies when it materialises a
    // card from a row, kept here so the two paths cannot drift.
    const sessionRowStatus = rowStatusOf(row);
    const status = cardStatus ?? sessionRowStatus;
    // JAISIU-fleet-map-idle-demote — when the card carries a raw
    // "running" / "queued" / "spawning" status BUT the phase
    // resolver has demoted it to "idle" (the post-restart path: a
    // restored card has no runId, no fresh stream, and is stale),
    // the graph node MUST reflect the demotion. The badge text and
    // class for the node are derived from this flag in the Fleet Map
    // view (the table already does this via the same
    // `resolveSubagentActivityPhase` call). Keeping the raw `status`
    // too is intentional — wire-truth for callers that don't yet
    // know about the demotion.
    // JAISIU-fleet-map-idle-demote — only meaningful when the node
    // has a card to demote. For nodes that fall back to the session
    // row's status (no live card), the demotion concept does not
    // apply — the row already carries a terminal/in-flight status
    // that the Fleet Map can render directly. Leaving `idleDemoted`
    // undefined (rather than `false`) is the contract callers
    // branch on.
    const phase = card ? resolveSubagentActivityPhase(card) : undefined;
    const idleDemoted = phase === "idle" ? true : undefined;
    const active = card ? phase !== "idle" && isActiveStatus(status) : isActiveStatus(status);

    nodes.push({
      id: nodeId,
      label: card ? subagentRunTitle(card) : (row?.label ?? row?.displayName ?? "Subagent"),
      subtitle: `@${agentId}`,
      kind: "subagent",
      status,
      idleDemoted,
      // JAISIU-fleet-map-idle-demote — surface `active` on the node so
      // external callers (Fleet Map view, tests, future consumers)
      // can read the demoted-at-source truth without re-implementing
      // the phase logic. The edges also carry `active` for the same
      // demoted decision.
      active,
      task: card?.task ?? row?.lastMessagePreview ?? "",
      sessionKey,
      spawnedBy,
      childSessionKeys: [],
      depth,
      cardId: card?.id,
      agentId,
    });
    registerChild(spawnedBy, sessionKey);

    let fromNodeId = parentId;
    if (card?.orchestrationId && spawnedBy === fleetRootSessionKey) {
      const orchId = orchNodeIds.get(card.orchestrationId);
      if (orchId) {
        fromNodeId = orchId;
      }
    } else if (isSubagentSessionKey(spawnedBy)) {
      fromNodeId = nodeIdForSession(spawnedBy);
    }

    edges.push({
      from: fromNodeId,
      to: nodeId,
      label: card ? `@${card.agentId}` : "spawn",
      childSessionKey: sessionKey,
      active,
      nested: spawnedBy !== fleetRootSessionKey && isSubagentSessionKey(spawnedBy),
    });
  }

  for (const card of cards) {
    if (!card.childSessionKey && card.status === "spawning") {
      const nodeId = `pending:${card.id}`;
      nodes.push({
        id: nodeId,
        label: subagentRunTitle(card),
        subtitle: `@${card.agentId} · spawning`,
        kind: "subagent",
        status: card.status,
        task: card.task,
        spawnedBy: focusSessionKey,
        childSessionKeys: [],
        depth: (depthBySession.get(focusSessionKey) ?? 0) + 1,
        cardId: card.id,
        agentId: card.agentId,
      });
      const fromId = isSubagentSessionKey(focusSessionKey)
        ? nodeIdForSession(focusSessionKey)
        : parentId;
      edges.push({
        from: fromId,
        to: nodeId,
        label: `@${card.agentId}`,
        active: true,
      });
      registerChild(focusSessionKey, nodeId);
    }
  }

  for (const node of nodes) {
    if (node.kind === "orchestration") {
      node.childSessionKeys = childKeysByParent.get(node.id) ?? node.childSessionKeys;
    } else if (node.sessionKey) {
      node.childSessionKeys = (childKeysByParent.get(node.sessionKey) ?? []).filter((k) =>
        k.startsWith("agent:"),
      );
    }
  }
  const parentNode = nodes.find((n) => n.kind === "parent");
  if (parentNode) {
    parentNode.childSessionKeys = childKeysByParent.get(fleetRootSessionKey) ?? [];
  }

  const maxDepth = nodes.reduce((m, n) => Math.max(m, n.depth), 0);
  const levels: DelegationGraphNode[][] = [];
  for (let d = 0; d <= maxDepth; d += 1) {
    const row = nodes.filter((n) => n.depth === d);
    if (row.length > 0) {
      levels.push(row);
    }
  }
  if (levels.length === 0 && nodes.length > 0) {
    levels.push(nodes);
  }

  return {
    fleetRootSessionKey,
    focusSessionKey,
    parentAgentId,
    parentLabel: parentAgentId,
    nodes,
    edges,
    levels,
  };
}
